import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "../common/axios";
import { Link as RouterLink, useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { useSetRecoilState } from "recoil";
import { userState } from "../common/atom/userState";
import { Fingerprint } from "lucide-react";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import { Separator } from "src/components/ui/separator";

export default function SignIn() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [redirectPath, setRedirectPath] = useState("/loa/all");
  const setUser = useSetRecoilState(userState);
  const ssoPopupRef = useRef(null);

  const navigate = useNavigate(); // useNavigate 사용
  const location = useLocation();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const path = queryParams.get("redirect");
    const computedPath = path && path !== "/" ? path : "/loa/all";
    setRedirectPath(computedPath);
  }, [location]);

  const openKnoxSSO = () => {
    if (
      /Mobi|Android|iPhone|iPad|iPod/i.test(
        navigator.userAgent,
      ) /* || window.innerWidth < 768 */
    ) {
      Swal.fire({
        title: "지원하지 않는 환경",
        text: "Knox SSO 로그인은 PC 환경에서만 지원됩니다.",
        icon: "warning",
        confirmButtonText: "확인",
      });
      return;
    }

    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    ssoPopupRef.current = window.open(
      "/sso-popup",
      "KnoxSSO",
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`,
    );
  };

  useEffect(() => {
    const handler = (event) => {
      if (event.origin !== window.location.origin) return;
      const { type, error, email: ssoEmail } = event.data || {};
      if (type === "SSO_SUCCESS") {
        navigate(redirectPath);
      } else if (type === "SSO_NO_USER") {
        navigate("/signup?email=" + encodeURIComponent(ssoEmail || ""));
      } else if (type === "SSO_ERROR") {
        Swal.fire({
          title: "오류",
          text: error || "Knox SSO 인증에 실패했습니다.",
          icon: "error",
          confirmButtonText: "확인",
        });
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [navigate, redirectPath]);

  const buttonDisabled = useMemo(() => {
    if (username.trim().length === 0 || password.trim().length === 0)
      return true;

    return false;
  }, [username, password]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const loginResponse = await axios.post(
        `/loa_login`,
        { username, password },
        { withCredentials: true },
      );

      if (loginResponse.data.success) {
        // Fetch user data after successful login
        setUser((prev) => {
          return { ...prev, username };
        });
        Swal.fire({
          title: "로그인 성공!",
          text: loginResponse.data.message,
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => {
          navigate(redirectPath);
        });
      } else {
        Swal.fire({
          title: "문제가 있습니다!",
          text: loginResponse.data.message,
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } catch (err) {
      console.log("err", err);

      const errorMessage =
        err.response?.data?.error || "로그인 중 문제가 발생했습니다.";

      // 비밀번호 만료 오류에 대한 처리 추가
      if (err.response?.data?.passwordExpired) {
        Swal.fire({
          title: "비밀번호 만료!",
          text: "비밀번호가 만료되었습니다. 비밀번호를 재설정해주세요.",
          icon: "warning",
          confirmButtonText: "비밀번호 재설정",
        }).then((result) => {
          if (result.isConfirmed) {
            // 사용자가 확인 버튼을 클릭하면 비밀번호 재설정 페이지로 리다이렉션
            navigate("/reset-password");
          }
        });
      } else {
        // 다른 종류의 오류에 대한 SweetAlert 표시
        Swal.fire({
          title: "오류!",
          text: errorMessage,
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    }
  };

  return (
    <div className="flex min-h-screen">
      <div className="flex flex-1 flex-col items-center justify-center px-6 py-12">
        <div className="w-full max-w-[336px]">
          <div className="text-center mb-7">
            <div className="flex items-center justify-center mb-4">
              <a href="http://www.samsungshi.com/kor/default.aspx">
                <img alt="Logo" src="/images/g1313.png" className="h-8 w-auto" />
              </a>
            </div>
            <h1 className="text-foreground font-extrabold text-[19px] tracking-tight mb-2">
              삼성중공업 현장공무 LOA 결재 시스템
            </h1>
            <p className="text-muted-foreground text-sm">계정에 로그인하세요</p>
          </div>

          <div className="rounded-lg border bg-card p-6">
            <h2 className="text-foreground font-bold text-base mb-1">로그인</h2>
            <p className="text-muted-foreground text-xs mb-5">
              아이디와 비밀번호를 입력하세요.
            </p>

            <form onSubmit={handleSubmit}>
              <div className="mb-3.5">
                <Label htmlFor="username" className="mb-1.5 block">
                  아이디
                </Label>
                <Input
                  id="username"
                  type="text"
                  name="username"
                  placeholder="아이디 (Dashboard 계정 사용)"
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="off"
                />
              </div>
              <div className="mb-3.5">
                <Label htmlFor="password" className="mb-1.5 block">
                  비밀번호
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="비밀번호"
                  name="password"
                  autoComplete="off"
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              <div className="flex justify-end mb-4 -mt-1.5">
                <RouterLink
                  className="text-muted-foreground hover:text-foreground hover:underline text-xs"
                  to={{ pathname: "/reset-password" }}
                >
                  비밀번호를 잊으셨나요?
                </RouterLink>
              </div>
              <Button
                type="submit"
                disabled={buttonDisabled}
                className="w-full bg-[hsl(222_47%_18%)] text-white hover:bg-[hsl(222_47%_24%)]"
              >
                로그인
              </Button>

              <div className="flex items-center gap-2.5 my-4">
                <Separator className="flex-1 w-auto" />
                <span className="text-muted-foreground text-[11px] uppercase tracking-wide">
                  또는
                </span>
                <Separator className="flex-1 w-auto" />
              </div>

              <Button
                type="button"
                variant="outline"
                onClick={openKnoxSSO}
                className="w-full"
              >
                <Fingerprint className="mr-2" size={18} />
                Knox SSO 로그인
              </Button>
            </form>
          </div>

          <p className="text-muted-foreground text-center text-xs mt-4">
            아직 계정이 없으신가요?{" "}
            <RouterLink
              className="text-primary font-semibold hover:underline"
              to={{ pathname: "/signup" }}
            >
              회원가입
            </RouterLink>
          </p>
        </div>
      </div>

      <div className="hidden lg:flex flex-1 items-center justify-center bg-[hsl(222_47%_18%)] px-12">
        <div className="max-w-[340px] text-center">
          <h3 className="text-white font-bold text-2xl tracking-tight mb-3 text-balance">
            구매·계약 결재를 하나의 흐름으로
          </h3>
          <p className="text-white/70 text-sm leading-7">
            Oracle Unifier와 연동되어 입찰·계약·기성·선급금 품의를 실시간으로
            관리합니다. 등록된 계정으로 로그인해 주세요.
          </p>
        </div>
      </div>
    </div>
  );
}
